import { ref, computed } from 'vue'
import { useSupabaseClient } from '#imports'
import useAuth from './useAuth'

export default function useRoles() {
  const supabase = useSupabaseClient()
  const { user } = useAuth()
  
  const userRole = ref(null)
  const loading = ref(false)
  const error = ref(null)
  
  // Create computed property for admin status
  const isAdmin = computed(() => userRole.value === 'admin')
  
  // Check user's role
  const checkRole = async (forceCheck = false) => {
    // If we already have the role and don't need to force a check, return it
    if (userRole.value && !forceCheck) {
      return { role: userRole.value, isAdmin: userRole.value === 'admin' }
    }
    
    loading.value = true
    error.value = null
    
    try {
      // Get session first to ensure we have authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (sessionError) {
        console.error('[ROLES] Session error:', sessionError.message)
        userRole.value = null
        return { role: null, isAdmin: false }
      }
      
      if (!session) {
        console.log('[ROLES] No active session')
        userRole.value = null
        return { role: null, isAdmin: false }
      }
      
      // Get user from session
      const userId = session.user.id
      
      if (!userId) {
        console.log('[ROLES] No user ID in session')
        userRole.value = null
        return { role: null, isAdmin: false }
      }
      
      console.log('[ROLES] Checking role for user:', userId)
      
      // Query the user_roles table for the user
      const { data, error: queryError } = await supabase
        .from('user_roles')
        .select('role_name')
        .eq('user_id', userId)
        .single()
      
      if (queryError) {
        // If no role record found, default to regular user
        if (queryError.code === 'PGRST116') {
          console.log('[ROLES] No role found for user, defaulting to "user"')
          userRole.value = 'user'
          return { role: 'user', isAdmin: false }
        }
        throw queryError
      }
      
      // Set the user's role (or default to 'user' if no role found)
      userRole.value = data?.role_name || 'user'
      console.log('[ROLES] User role:', userRole.value)
      
      return { role: userRole.value, isAdmin: userRole.value === 'admin' }
    } catch (err) {
      console.error('[ROLES] Role check error:', err.message)
      error.value = err.message
      return { role: 'user', isAdmin: false } // Default to regular user on error
    } finally {
      loading.value = false
    }
  }
  
  // Update a user's role (admin operation)
  const updateUserRole = async (targetUserId, newRole) => {
    // Validate role
    if (!['admin', 'user'].includes(newRole)) {
      throw new Error('Invalid role. Must be "admin" or "user".')
    }
    
    // Check if the current user is an admin
    const { isAdmin: currentUserIsAdmin } = await checkRole(true)
    
    if (!currentUserIsAdmin) {
      throw new Error('Only administrators can update user roles')
    }
    
    loading.value = true
    error.value = null
    
    try {
      // Check if the user already has a role
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', targetUserId)
        .single()
      
      let result
      
      if (existingRole) {
        // Update the existing role
        result = await supabase
          .from('user_roles')
          .update({ role_name: newRole })
          .eq('user_id', targetUserId)
      } else {
        // Insert a new role
        result = await supabase
          .from('user_roles')
          .insert({ user_id: targetUserId, role_name: newRole })
      }
      
      if (result.error) throw result.error
      
      // If updating the current user's role, update the local state
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      if (currentUser && targetUserId === currentUser.id) {
        userRole.value = newRole
      }
      
      return { success: true }
    } catch (err) {
      console.error('[ROLES] Role update error:', err.message)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }
  
  // Get all users with their roles (admin operation)
  const getAllUsersWithRoles = async () => {
    // Check if the current user is an admin
    const { isAdmin: currentUserIsAdmin } = await checkRole(true)
    
    if (!currentUserIsAdmin) {
      throw new Error('Only administrators can view all user roles')
    }
    
    loading.value = true
    error.value = null
    
    try {
      // Get all profiles first
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          full_name,
          created_at
        `)
        .order('created_at', { ascending: false })
      
      if (profilesError) throw profilesError
      
      // Then get all user roles in a separate query
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role_name')
      
      if (rolesError) throw rolesError
      
      // Create a map of user IDs to their roles for quick lookup
      const roleMap = {}
      userRoles?.forEach(role => {
        roleMap[role.user_id] = role.role_name
      })
      
      // Format data to include role information
      const formattedData = profiles.map(user => ({
        id: user.id,
        email: user.email,
        fullName: user.full_name,
        createdAt: user.created_at,
        role: roleMap[user.id] || 'user' // Default to 'user' if no role assigned
      }))
      
      // Log successful operation
      await logRoleOperation('get_all_users_with_roles', null, {
        request: { action: 'getAllUsersWithRoles' },
        response: { userCount: formattedData.length }
      })
      
      return { users: formattedData, success: true }
    } catch (err) {
      console.error('[ROLES] Get all users error:', err.message)
      error.value = err.message
      
      // Log error
      await logRoleOperation('get_all_users_with_roles_error', null, {
        request: { action: 'getAllUsersWithRoles' },
        response: { error: err.message }
      })
      
      return { users: [], success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }
  
  // Log to app_log function
  const logRoleOperation = async (operation, targetUserId, details) => {
    try {
      const { data: { VERBOSE_API_LOGGING } } = useRuntimeConfig().public
      
      const logLevel = operation.includes('error') ? 'error' : 'debug'
      const shouldLogDetails = VERBOSE_API_LOGGING === 'true' && details
      
      // Get current user ID directly from Supabase if needed
      let userId = user.value?.id
      if (!userId) {
        const { data: { user: authUser } } = await supabase.auth.getUser()
        userId = authUser?.id
      }
      
      await supabase.from('app_log').insert({
        level: logLevel,
        module: 'user_roles',
        user_id: userId,
        tag: operation,
        request: shouldLogDetails ? details.request : null,
        response: shouldLogDetails ? details.response : null
      })
    } catch (err) {
      console.error('[ROLES] Logging error:', err.message)
    }
  }
  
  return {
    userRole: computed(() => userRole.value),
    isAdmin,
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    checkRole,
    updateUserRole,
    getAllUsersWithRoles,
    logRoleOperation
  }
} 